/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react'
import { Field, ErrorMessage, FieldArray } from 'formik'
import {
   Grid,
   TextField,
   Box,
   Typography,
   Button,
   Select,
   MenuItem,
   InputAdornment,
   FormControl,
   FormHelperText,
   InputLabel,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { useSelector } from 'react-redux'
import { useGetCurrenciesQuery } from '../../Redux/Service/CurrencyApi'

function StepTwo({
   isSubmitting,
   values,
   setFieldValue,
   touched,
   errors,
   initialValues,
}) {
   // ─── StepFour state ───────────────────────────────────────────────────────
   const [selectedCurrency, setSelectedCurrency] = useState(null)
   const { data: currenciesData, isLoading, error } = useGetCurrenciesQuery()
   const currencies = currenciesData?.response ?? null

   // ─── StepFour: set default currency once ──────────────────────────────────
   useEffect(() => {
      if (currencies && currencies.length > 0 && !values.currencyId) {
         const defaultCurrency = currencies[0]
         setSelectedCurrency(defaultCurrency)
         setFieldValue('currencyId', defaultCurrency.id)
      }
   }, [currencies, setFieldValue, values.currencyId])

   // ─── StepFour: sync selectedCurrency with Formik value ───────────────────
   useEffect(() => {
      if (currencies && currencies.length > 0 && values.currencyId) {
         const selected = currencies.find(
            (currency) => currency.id === parseInt(values.currencyId),
         )
         if (selected) {
            setSelectedCurrency(selected)
         }
      }
   }, [currencies, values.currencyId])

   // ─── StepFour: currency change handler ───────────────────────────────────
   const handleCurrencyChange = (event) => {
      const selected = currencies?.find(
         (currency) => currency.id === parseInt(event.target.value),
      )
      setSelectedCurrency(selected)
      setFieldValue('currencyId', selected.id)
   }

   // ─── StepFour: tax calculations ───────────────────────────────────────────
   const calculatedTaxAmount =
      (values.total_development_cost * (values.payable_tax_percentage || 0)) /
      100
   const totalAmountIncludingTax =
      Number(values.total_development_cost) + Number(calculatedTaxAmount)

   // ─── StepFour: Redux prefilled currency ──────────────────────────────────
   const currencyDetails = useSelector(
      (state) => state.contractState?.quotation,
   )?.find((state) => state.id === values.quotation_id)?.estimate?.currency

   // ─── StepTwo: one-time default note initializer ───────────────────────────
   useEffect(() => {
      const alreadyInitialized = values?._noteInitialized
      const currentNote =
         typeof values.note === 'string' ? values.note.trim() : values.note

      if (!alreadyInitialized) {
         if (
            currentNote === undefined ||
            currentNote === null ||
            currentNote === ''
         ) {
            setFieldValue(
               'note',
               'Note: Detailed Delivery Milestone has been shared in Addendum B.',
               false,
            )
         }
         setFieldValue('_noteInitialized', true, false)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   // ─── StepTwo: remove item from FieldArray ─────────────────────────────────
   const handleRemoveItem = (id, arrayHelpers) => {
      arrayHelpers.remove(id)
   }

   // ─── StepTwo: milestone cost totals ──────────────────────────────────────
   const totalMilestoneCost = values.deliverable_milestones.reduce(
      (sum, milestone) => sum + (Number(milestone.cost) || 0),
      0,
   )
   const commencementValue = Number(values.commencementValue) || 0
   const totalWithCommencement = totalMilestoneCost + commencementValue

   // ─── StepTwo: currency fallback from contractState ────────────────────────
   const currenciesFromContract = useSelector(
      (state) => state.contractState?.quotation?.[0]?.estimate?.currency,
   )

   // ─── StepTwo: derive currencyCode for cost labels ─────────────────────────
   // Priority: selectedCurrency (kept in sync by StepFour logic above) -> contractState -> 'USD'
   const currencyCode =
      selectedCurrency?.symbol ||
      selectedCurrency?.code ||
      (Array.isArray(currenciesFromContract)
         ? currenciesFromContract[0]?.symbol || currenciesFromContract[0]?.code
         : currenciesFromContract?.symbol || currenciesFromContract?.code) ||
      'USD'

   // ─── StepTwo: dm_structure conditional validation helpers ─────────────────
   const isDMItemFilled = (item) => {
      if (!item) return false
      const milnameFilled =
         typeof item.milname === 'string' && item.milname.trim() !== ''
      const deliveryFilled =
         item.delivery !== undefined &&
         item.delivery !== null &&
         String(item.delivery).trim() !== '' &&
         Number(item.delivery) > 0
      const typeFilled =
         typeof item.type === 'string' && item.type.trim() !== ''
      return milnameFilled || deliveryFilled || typeFilled
   }

   const isFieldMissingWhenRequired = (item, fieldName) => {
      if (!isDMItemFilled(item)) return false
      if (fieldName === 'milname') {
         return !(item.milname && item.milname.trim() !== '')
      }
      if (fieldName === 'delivery') {
         return !(
            item.delivery !== undefined &&
            item.delivery !== null &&
            String(item.delivery).trim() !== '' &&
            Number(item.delivery) > 0
         )
      }
      if (fieldName === 'type') {
         return !(item.type && item.type.trim() !== '')
      }
      return false
   }

   return (
      <>
         <div className="card stepCard">
            <Box className="card-body" sx={{ padding: '15px' }}>
               <Grid container spacing={2}>

                  {/* ── COMMERCIAL DETAILS (from StepFour) ─────────────────── */}
                  <Grid item md={12} xs={12}>
                     <Box className="commonBox">
                        <Grid container spacing={3}>
                           <Grid item xs={12}>
                              <Typography variant="h6" gutterBottom align="left">
                                 Commercial Details
                              </Typography>
                              <Grid container spacing={2} sx={{ my: 1 }}>
                                 <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                       <Grid item md={6} xs={12}>
                                          <FormControl fullWidth variant="outlined">
                                             <label htmlFor="currencyLabel">
                                                Select Currency{' '}
                                                <span style={{ color: 'red' }}>*</span>
                                             </label>
                                             <InputLabel
                                                id="currency-label"
                                                sx={{ top: '31px' }}
                                             >
                                                Select Currency{' '}
                                                <span style={{ color: 'red' }}>*</span>
                                             </InputLabel>
                                             {isLoading ? (
                                                <p>Loading currencies...</p>
                                             ) : error ? (
                                                <p>Error fetching currencies</p>
                                             ) : (
                                                <Select
                                                   labelId="currency-label"
                                                   id="currency"
                                                   value={values.currencyId || ''}
                                                   onChange={handleCurrencyChange}
                                                   label="Select Currency *"
                                                >
                                                   {currencies?.map((currency) => (
                                                      <MenuItem
                                                         key={currency.id}
                                                         value={currency.id}
                                                      >
                                                         {currency.name} ({currency.symbol})
                                                      </MenuItem>
                                                   ))}
                                                </Select>
                                             )}
                                          </FormControl>
                                       </Grid>

                                       <Grid item md={6} xs={12}>
                                          <label htmlFor="total_development_cost">
                                             Total Development Cost{' '}
                                             <span style={{ color: 'red' }}>*</span>
                                          </label>
                                          <Field
                                             fullWidth
                                             label={
                                                currencyDetails
                                                   ? `Total Development Cost (in ${selectedCurrency?.symbol || currencyDetails?.code}) *`
                                                   : `Total Development Cost *`
                                             }
                                             name="total_development_cost"
                                             variant="outlined"
                                             type="number"
                                             value={values.total_development_cost}
                                             onChange={(e) => {
                                                setFieldValue(
                                                   'total_development_cost',
                                                   e.target.value,
                                                )
                                             }}
                                             onWheel={(e) => e.target.blur()}
                                             onFocus={(e) => e.target.select()}
                                             as={TextField}
                                             error={
                                                touched.total_development_cost &&
                                                Boolean(errors.total_development_cost)
                                             }
                                             helperText={
                                                touched.total_development_cost &&
                                                errors.total_development_cost
                                             }
                                          />
                                          {selectedCurrency?.name && (
                                             <Typography
                                                variant="caption"
                                                sx={{
                                                   display: 'block',
                                                   color: '#222',
                                                   fontWeight: 600,
                                                   mt: 0.5,
                                                }}
                                             >
                                                {`Total Development Cost (in ${selectedCurrency?.symbol || selectedCurrency?.code || selectedCurrency?.name})`}
                                             </Typography>
                                          )}
                                       </Grid>

                                       <Grid item md={6} xs={12}>
                                          <label htmlFor="payable_tax_percentage">
                                             Payable Tax Percentage (%)
                                          </label>
                                          <Field
                                             fullWidth
                                             label="Payable Tax Percentage (%)"
                                             name="payable_tax_percentage"
                                             variant="outlined"
                                             type="number"
                                             value={values.payable_tax_percentage}
                                             onChange={(e) => {
                                                const value = Math.min(e.target.value, 100)
                                                setFieldValue('payable_tax_percentage', value)
                                             }}
                                             onWheel={(e) => e.target.blur()}
                                             onFocus={(e) => e.target.select()}
                                             as={TextField}
                                             error={
                                                touched.payable_tax_percentage &&
                                                Boolean(errors.payable_tax_percentage)
                                             }
                                             helperText={
                                                touched.payable_tax_percentage &&
                                                errors.payable_tax_percentage
                                             }
                                          />
                                       </Grid>

                                       <Grid item md={6} xs={12}>
                                          <label htmlFor="taxAmount">Tax Amount</label>
                                          <Typography
                                             variant="body1"
                                             style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                backgroundColor: '#f5f5f5',
                                             }}
                                          >
                                             {selectedCurrency?.code
                                                ? `${selectedCurrency.code} ${Number(calculatedTaxAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                : calculatedTaxAmount}
                                          </Typography>
                                       </Grid>

                                       <Grid item md={6} xs={12}>
                                          <label htmlFor="totalAmount">
                                             Total Amount Including Tax
                                          </label>
                                          <Typography
                                             variant="body1"
                                             style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #e0e0e0',
                                                borderRadius: '4px',
                                                backgroundColor: '#f5f5f5',
                                             }}
                                          >
                                             {selectedCurrency?.code
                                                ? `${selectedCurrency.code} ${Number(totalAmountIncludingTax).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                : totalAmountIncludingTax}
                                          </Typography>
                                       </Grid>
                                    </Grid>
                                 </Grid>
                              </Grid>
                           </Grid>
                        </Grid>
                     </Box>
                  </Grid>

                  {/* ── PROJECT DEVELOPMENT TIME (from StepTwo) ────────────── */}
                  <Grid item xs={12}>
                     <Box className="commonBox">
                        <Typography variant="h6" gutterBottom align="left">
                           Project Development Time{' '}
                           <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Field
                           fullWidth
                           label={
                              <>
                                 Project Development Time{' '}
                                 <span style={{ color: 'red' }}>*</span>
                              </>
                           }
                           name="project_development"
                           variant="outlined"
                           placeholder="Enter Project development time (e.g 6 months)"
                           as={TextField}
                           error={
                              touched.project_development &&
                              Boolean(errors.project_development)
                           }
                           helperText={
                              touched.project_development &&
                                 errors.project_development ? errors.project_development : 'e.g 6  months development time'
                           }
                        />
                     </Box>
                  </Grid>

                  {/* ── DELIVERABLE MILESTONES / DM STRUCTURE (from StepTwo) ── */}
                  <Grid item xs={12}>
                     <Box className="commonBox">
                        <Typography variant="h6" gutterBottom align="left">
                           Deliverable Milestones{' '}
                        </Typography>
                        <FieldArray name="dm_structure">
                           {(arrayHelpers) => (
                              <div>
                                 {!values?.dm_structure ||
                                    values.dm_structure.length === 0 ? (
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          justifyContent: 'flex-end',
                                       }}
                                    >
                                       <Button
                                          onClick={() =>
                                             arrayHelpers.push({
                                                milname: '',
                                                delivery: 0,
                                                type: 'Days',
                                             })
                                          }
                                          startIcon={<AddIcon />}
                                          variant="contained"
                                          className="addFieldBtn"
                                       >
                                          Add Milestone
                                       </Button>
                                    </Box>
                                 ) : (
                                    values?.dm_structure?.map((val, index) => {
                                       const dmItem =
                                          values?.dm_structure?.[index] || {}
                                       const milnameMissing =
                                          isFieldMissingWhenRequired(dmItem, 'milname')
                                       const deliveryMissing =
                                          isFieldMissingWhenRequired(dmItem, 'delivery')
                                       const typeMissing =
                                          isFieldMissingWhenRequired(dmItem, 'type')

                                       return (
                                          <Grid
                                             container
                                             spacing={2}
                                             key={val.id || index}
                                             sx={{ my: 1 }}
                                          >
                                             <Grid item xs={12}>
                                                <Grid container spacing={2}>
                                                   <Grid item xs={5}>
                                                      <Field
                                                         fullWidth
                                                         label="Milestone"
                                                         name={`dm_structure.${index}.milname`}
                                                         value={val.milname}
                                                         variant="outlined"
                                                         placeholder="Enter Milestone Name"
                                                         as={TextField}
                                                         error={
                                                            (touched.dm_structure?.[index]?.milname &&
                                                               Boolean(errors.dm_structure?.[index]?.milname)) ||
                                                            milnameMissing
                                                         }
                                                         helperText={
                                                            (touched.dm_structure?.[index]?.milname &&
                                                               errors.dm_structure?.[index]?.milname) ||
                                                            (milnameMissing
                                                               ? 'This field is required when any other field in the row is filled.'
                                                               : '')
                                                         }
                                                      />
                                                   </Grid>

                                                   <Grid item xs={3}>
                                                      <Field
                                                         fullWidth
                                                         label="Delivery"
                                                         name={`dm_structure.${index}.delivery`}
                                                         variant="outlined"
                                                         placeholder="Enter delivery time in numbers"
                                                         type="number"
                                                         as={TextField}
                                                         onWheel={(e) => e.target.blur()}
                                                         onFocus={(e) => e.target.select()}
                                                         inputProps={{ step: '1', min: 0 }}
                                                         error={
                                                            (touched.dm_structure?.[index]?.delivery &&
                                                               Boolean(errors.dm_structure?.[index]?.delivery)) ||
                                                            deliveryMissing
                                                         }
                                                         helperText={
                                                            (touched.dm_structure?.[index]?.delivery &&
                                                               errors.dm_structure?.[index]?.delivery) ||
                                                            (deliveryMissing
                                                               ? 'This field is required when any other field in the row is filled and must be > 0.'
                                                               : '')
                                                         }
                                                      />
                                                      <Typography
                                                         variant="caption"
                                                         color="GrayText"
                                                         sx={{ mt: 0.5, display: 'block' }}
                                                      >
                                                         (e.g. 10)
                                                      </Typography>
                                                   </Grid>

                                                   <Grid item xs={2}>
                                                      <FormControl
                                                         fullWidth
                                                         variant="outlined"
                                                         error={
                                                            (touched.dm_structure?.[index]?.type &&
                                                               Boolean(errors.dm_structure?.[index]?.type)) ||
                                                            typeMissing
                                                         }
                                                      >
                                                         <Select
                                                            displayEmpty
                                                            value={val.type || ''}
                                                            onChange={(e) =>
                                                               setFieldValue(
                                                                  `dm_structure.${index}.type`,
                                                                  e.target.value,
                                                               )
                                                            }
                                                            renderValue={(selected) =>
                                                               selected ? selected : 'Select Type'
                                                            }
                                                         >
                                                            <MenuItem value="Days">Days</MenuItem>
                                                            <MenuItem value="Month">Month</MenuItem>
                                                            <MenuItem value="Weeks">Weeks</MenuItem>
                                                         </Select>
                                                         <FormHelperText>
                                                            {(touched.dm_structure?.[index]?.type &&
                                                               errors.dm_structure?.[index]?.type) ||
                                                               (typeMissing
                                                                  ? 'This field is required when any other field in the row is filled.'
                                                                  : '')}
                                                         </FormHelperText>
                                                      </FormControl>
                                                   </Grid>

                                                   <Grid item xs={2} align="right">
                                                      <Box
                                                         sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            justifyContent: 'flex-end',
                                                         }}
                                                      >
                                                         {index === 0 && (
                                                            <Button
                                                               onClick={() =>
                                                                  arrayHelpers.push({
                                                                     milname: '',
                                                                     delivery: 0,
                                                                     type: 'Days',
                                                                  })
                                                               }
                                                               startIcon={<AddIcon />}
                                                               variant="contained"
                                                               className="addFieldBtn"
                                                            ></Button>
                                                         )}
                                                         <Button
                                                            onClick={() =>
                                                               handleRemoveItem(index, arrayHelpers)
                                                            }
                                                            startIcon={<DeleteIcon />}
                                                            variant="contained"
                                                            className="deleteFieldBtn"
                                                         ></Button>
                                                      </Box>
                                                   </Grid>
                                                </Grid>
                                             </Grid>
                                          </Grid>
                                       )
                                    })
                                 )}
                              </div>
                           )}
                        </FieldArray>
                     </Box>
                  </Grid>

                  {/* ── COMMENCEMENT FEES (from StepTwo) ───────────────────── */}
                  <Grid item xs={12}>
                     <Box className="commonBox">
                        <Typography variant="h6" gutterBottom align="left">
                           Commencement Fees{' '}
                           <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <Grid container spacing={2} alignItems="flex-start">
                           <Grid item xs={6} md={4}>
                              <Field
                                 fullWidth
                                 label={
                                    <>
                                       Commencement Label{' '}
                                       <span style={{ color: 'red' }}>*</span>
                                    </>
                                 }
                                 name="commencementLabel"
                                 as={TextField}
                                 variant="outlined"
                                 size="medium"
                                 placeholder="Enter commencement label"
                                 error={
                                    touched.commencementLabel &&
                                    Boolean(errors.commencementLabel)
                                 }
                                 helperText={
                                    touched.commencementLabel && errors.commencementLabel
                                 }
                                 InputLabelProps={{ shrink: true }}
                              />
                              <Typography
                                 variant="caption"
                                 color="GrayText"
                                 sx={{ mt: 0.5, display: 'block' }}
                              >
                                 (e.g. Deposit, Security, etc.)
                              </Typography>
                           </Grid>
                           <Grid item xs={6} md={4}>
                              <Field
                                 fullWidth
                                 label={
                                    <>
                                       Commencement Value{' '}
                                       <span style={{ color: 'red' }}>*</span>
                                    </>
                                 }
                                 name="commencementValue"
                                 type="number"
                                 as={TextField}
                                 variant="outlined"
                                 placeholder="Enter commencement value in numbers"
                                 size="medium"
                                 onWheel={(e) => e.target.blur()}
                                 onFocus={(e) => e.target.select()}
                                 inputProps={{ step: '0.01', min: 0 }}
                                 error={
                                    touched.commencementValue &&
                                    Boolean(errors.commencementValue)
                                 }
                                 helperText={
                                    touched.commencementValue && errors.commencementValue
                                 }
                                 InputLabelProps={{ shrink: true }}
                              />
                              <Typography
                                 variant="caption"
                                 color="GrayText"
                                 sx={{ mt: 0.5, display: 'block' }}
                              >
                                 (e.g. 1000)
                              </Typography>
                           </Grid>
                        </Grid>
                     </Box>
                  </Grid>

                  {/* ── PAYMENT MILESTONES + NOTE (from StepTwo) ───────────── */}
                  <Grid item xs={12}>
                     <Box className="commonBox">
                        <Typography variant="h6" gutterBottom align="left">
                           Payment Milestones{' '}
                           <span style={{ color: 'red' }}>*</span>
                        </Typography>
                        <FieldArray name="deliverable_milestones">
                           {(arrayHelpers) => (
                              <div>
                                 {values?.deliverable_milestones?.map((val, index) => {
                                    const milestoneTooLong = String(val.milestone || '').length > 50;
                                    let milestoneEmpty

                                    if (touched.deliverable_milestones?.[index]?.milestone) {
                                       milestoneEmpty =
                                          !val.milestone || String(val.milestone).trim() === '';
                                    }
                                    let costInvalid

                                    if (touched.deliverable_milestones?.[index]?.cost) {
                                       costInvalid =
                                          val.cost === '' ||
                                          val.cost === null ||
                                          val.cost === undefined ||
                                          Number(val.cost) <= 0;
                                    }
                                    return (
                                       <Grid
                                          container
                                          spacing={2}
                                          key={val.id}
                                          sx={{ my: 1 }}
                                       >
                                          <Grid item xs={12} spacing={2}>
                                             <Grid container spacing={2}>
                                                <Grid item xs={3}>
                                                   <Field
                                                      fullWidth
                                                      label={
                                                         <>
                                                            Milestone{' '}
                                                            <span style={{ color: 'red' }}>*</span>
                                                         </>
                                                      }
                                                      name={`deliverable_milestones.${index}.milestone`}
                                                      value={
                                                         values.deliverable_milestones[index].milestone
                                                      }
                                                      variant="outlined"
                                                      placeholder="Enter Payment milestone"
                                                      as={TextField}
                                                      error={
                                                         milestoneEmpty ||
                                                         milestoneTooLong ||
                                                         (touched.deliverable_milestones?.[index]?.milestone &&
                                                            Boolean(
                                                               errors.deliverable_milestones?.[index]?.milestone,
                                                            ))
                                                      }
                                                      // NEW: shows the message immediately when empty, regardless of touched
                                                      helperText={
                                                         milestoneEmpty
                                                            ? 'Milestone name is required'
                                                            : milestoneTooLong
                                                               ? 'Milestone name should be within 50 characters'
                                                               : (touched.deliverable_milestones?.[index]?.milestone &&
                                                                  errors.deliverable_milestones?.[index]?.milestone) ||
                                                               ''

                                                      }
                                                   />
                                                </Grid>

                                                <Grid item xs={2}>
                                                   <Field
                                                      fullWidth
                                                      label={
                                                         <>
                                                            {`Cost (${currencyCode}) `}
                                                            <span style={{ color: 'red' }}>*</span>
                                                         </>
                                                      }
                                                      name={`deliverable_milestones.${index}.cost`}
                                                      variant="outlined"
                                                      type="number"
                                                      value={
                                                         values.deliverable_milestones[index].cost
                                                      }
                                                      as={TextField}
                                                      onWheel={(e) => e.target.blur()}
                                                      onFocus={(e) => e.target.select()}
                                                      error={
                                                         costInvalid ||
                                                         (touched.deliverable_milestones?.[index]?.cost &&
                                                            Boolean(errors.deliverable_milestones?.[index]?.cost))
                                                      }
                                                      // NEW: shows the message immediately when empty/0, regardless of touched
                                                      helperText={
                                                         costInvalid
                                                            ? 'Cost must be greater than 0'
                                                            : (touched.deliverable_milestones?.[index]?.cost &&
                                                               errors.deliverable_milestones?.[index]?.cost) ||
                                                            ''
                                                      }
                                                   />
                                                </Grid>

                                                <Grid item xs={1}></Grid>
                                                <Grid item xs={2} align="right">
                                                   {index === 0 ? (
                                                      <Grid item xs={12}>
                                                         <Button
                                                            type="button"
                                                            className="addFieldBtn"
                                                            onClick={() =>
                                                               arrayHelpers.push({
                                                                  id:
                                                                     values.deliverable_milestones
                                                                        .length + 1,
                                                                  milestone: '',
                                                                  cost: 0,
                                                               })
                                                            }
                                                         >
                                                            <AddIcon />
                                                         </Button>
                                                      </Grid>
                                                   ) : (
                                                      <Grid item xs={12}>
                                                         <Button
                                                            type="button"
                                                            className="deleteFieldBtn"
                                                            onClick={() =>
                                                               handleRemoveItem(index, arrayHelpers)
                                                            }
                                                         >
                                                            <DeleteIcon />
                                                         </Button>
                                                      </Grid>
                                                   )}
                                                </Grid>
                                             </Grid>
                                          </Grid>
                                       </Grid>
                                    )
                                 })}

                                 {/* Total Cost Field */}
                                 <Grid item xs={12} spacing={2} sx={{ marginTop: "40px" }}>
                                    <TextField
                                       fullWidth
                                       label={
                                          <>
                                             {`Total Milestone Cost (${currencyCode}) `}
                                             <span style={{ color: 'red' }}>*</span>
                                          </>
                                       }
                                       value={totalMilestoneCost.toFixed(2)}
                                       variant="outlined"
                                       InputProps={{ readOnly: true }}
                                    />
                                 </Grid>
                                 <Grid item xs={12} spacing={2}>
                                    <TextField
                                       fullWidth
                                       label={
                                          <>
                                             Total (Milestones + Commencement){' '}
                                             <span style={{ color: 'red' }}>*</span>
                                          </>
                                       }
                                       value={totalWithCommencement.toFixed(2)}
                                       variant="outlined"
                                       InputProps={{ readOnly: true }}
                                       sx={{ mt: 2 }}
                                    />
                                 </Grid>
                              </div>
                           )}
                        </FieldArray>

                        {/* Note field */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                           <Typography variant="h6" gutterBottom align="left">
                              Note
                           </Typography>
                           <Field
                              fullWidth
                              label={<>Note</>}
                              name="note"
                              variant="outlined"
                              as={TextField}
                              multiline
                              rows={4}
                              error={touched.note && Boolean(errors.note)}
                              helperText={touched.note && errors.note}
                           />
                           {values.note.length > 0 && (
                              <Typography
                                 variant="caption"
                                 color="GrayText"
                                 sx={{ mt: 0.5, display: 'block' }}
                              >
                                 {values.note?.length} characters
                              </Typography>
                           )}
                        </Grid>
                     </Box>
                  </Grid>

               </Grid>
            </Box>
         </div>
      </>
   )
}

export default StepTwo